/**
 * PhotosLog - Technical Photos Dashboard
 * Grid-based photo gallery with upload, filter, sort, search, and detail view.
 * Photos stored in SharePoint; thumbnails and full images served via backend proxy.
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { usePresence, ActiveViewers } from '../common/ViewingTemplate';
import PhotoDetailModal from './PhotoDetailModal';

const formatDate = (val) => {
  if (!val) return '';
  const d = new Date(val);
  return isNaN(d.getTime()) ? val : d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatDateTime = (val) => {
  if (!val) return '';
  const d = new Date(val);
  return isNaN(d.getTime()) ? val : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const SORT_OPTIONS = [
  { value: 'date_added', label: 'Date Added' },
  { value: 'date_taken', label: 'Date Taken' },
  { value: 'title', label: 'Title' },
  { value: 'file_name', label: 'File Name' },
  { value: 'category', label: 'Category' },
];

const REF_TYPES = [
  { value: 'RFI', label: 'RFI' },
  { value: 'ISO', label: 'ISO Sheet' },
  { value: 'TRANSMITTAL', label: 'Transmittal' },
  { value: 'TRACKER_PIPING', label: 'Piping Tracker' },
  { value: 'TRACKER_MECHANICAL', label: 'Mechanical Tracker' },
  { value: 'TRACKER_STRUCTURAL', label: 'Structural Tracker' },
  { value: 'DAILY_REPORT', label: 'Daily Report' },
  { value: 'QA_QC', label: 'QA/QC' },
  { value: 'OTHER', label: 'Other' },
];

const PhotosLog = ({ projectId }) => {
  const { instance, accounts } = useMsal();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  // Filters & sort
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [uploaderFilter, setUploaderFilter] = useState('');
  const [sortBy, setSortBy] = useState('date_added');
  const [sortOrder, setSortOrder] = useState('desc');
  const [categories, setCategories] = useState([]);
  const [uploaders, setUploaders] = useState([]);

  // Upload modal
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadForm, setUploadForm] = useState({ title: '', description: '', category: '', location: '', date_taken: '' });
  const [uploadRefs, setUploadRefs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  // Detail view
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  // View toggle
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'

  // Selection for bulk ops
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Live data push support
  const fetchPhotosRef = useRef(null);
  const handleDataChange = useCallback(() => {
    if (fetchPhotosRef.current) fetchPhotosRef.current();
  }, []);

  const { viewers, isConnected, connectionState, socketId } = usePresence(
    selectedPhoto ? 'photo' : 'technicalPhotos',
    selectedPhoto ? String(selectedPhoto.id) : (projectId ? String(projectId) : 'global'),
    true,
    handleDataChange
  );

  const socketIdRef = useRef(null);
  useEffect(() => { socketIdRef.current = socketId; }, [socketId]);

  const syncHeaders = useCallback(() => {
    return socketIdRef.current ? { headers: { 'x-socket-id': socketIdRef.current } } : {};
  }, []);

  const fetchPhotos = useCallback(async () => {
    if (!projectId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (categoryFilter) params.append('category', categoryFilter);
      if (uploaderFilter) params.append('uploaded_by', uploaderFilter);
      if (searchTerm) params.append('search', searchTerm);
      params.append('sort', sortBy);
      params.append('order', sortOrder);

      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/technical-photos/project/${projectId}?${params.toString()}`
      );
      if (response.data?.success) {
        setPhotos(response.data.data || []);
      }
    } catch (err) {
      console.error('Photos fetch error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load photos');
    } finally {
      setLoading(false);
    }
  }, [projectId, instance, accounts, categoryFilter, uploaderFilter, searchTerm, sortBy, sortOrder]);

  useEffect(() => { fetchPhotosRef.current = fetchPhotos; }, [fetchPhotos]);

  const fetchFilterOptions = useCallback(async () => {
    if (!projectId) return;
    try {
      const [catRes, uplRes] = await Promise.all([
        authenticatedApiCall(instance, accounts, 'GET', `/technical-photos/project/${projectId}/categories`),
        authenticatedApiCall(instance, accounts, 'GET', `/technical-photos/project/${projectId}/uploaders`),
      ]);
      if (catRes.data?.success) setCategories(catRes.data.categories || []);
      if (uplRes.data?.success) setUploaders(uplRes.data.uploaders || []);
    } catch (e) { /* filter options are optional */ }
  }, [projectId, instance, accounts]);

  useEffect(() => { fetchPhotos(); }, [fetchPhotos]);
  useEffect(() => { fetchFilterOptions(); }, [fetchFilterOptions]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  // Upload handlers
  const openUploadModal = () => {
    setShowUploadModal(true);
    setUploadFiles([]);
    setUploadForm({ title: '', description: '', category: '', location: '', date_taken: '' });
    setUploadRefs([]);
    setUploadError(null);
  };

  const handleFilesSelected = (fileList) => {
    const arr = Array.from(fileList).filter(f =>
      f.type.startsWith('image/') || /\.(jpe?g|png|gif|webp|heic|heif)$/i.test(f.name)
    );
    setUploadFiles(prev => [...prev, ...arr]);
  };

  const removeUploadFile = (idx) => {
    setUploadFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();
    if (!uploadFiles.length || !projectId) return;
    setUploading(true);
    setUploadError(null);
    try {
      const formData = new FormData();
      uploadFiles.forEach(f => formData.append('photos', f));
      if (uploadForm.title) formData.append('title', uploadForm.title);
      if (uploadForm.description) formData.append('description', uploadForm.description);
      if (uploadForm.category) formData.append('category', uploadForm.category);
      if (uploadForm.location) formData.append('location', uploadForm.location);
      if (uploadForm.date_taken) formData.append('date_taken', uploadForm.date_taken);

      const uploadRes = await authenticatedApiCall(instance, accounts, 'POST', `/technical-photos/project/${projectId}/upload`, formData, syncHeaders());

      // Attach references to each created photo
      const validRefs = uploadRefs.filter(r => r.linked_item_id || r.description);
      if (validRefs.length > 0 && uploadRes.data?.data) {
        const createdPhotos = Array.isArray(uploadRes.data.data) ? uploadRes.data.data : [uploadRes.data.data];
        for (const photo of createdPhotos) {
          for (const ref of validRefs) {
            try {
              await authenticatedApiCall(instance, accounts, 'POST', `/technical-photos/${photo.id}/tags`, {
                x_coordinate: -1,
                y_coordinate: -1,
                linked_item_type: ref.linked_item_type,
                linked_item_id: ref.linked_item_id,
                description: ref.description,
              }, syncHeaders());
            } catch (_) { /* best effort */ }
          }
        }
      }

      showToast(`${uploadFiles.length} photo(s) uploaded`);
      setShowUploadModal(false);
      fetchPhotos();
      fetchFilterOptions();
    } catch (err) {
      setUploadError(err.response?.data?.message || err.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photoId) => {
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/technical-photos/photo/${photoId}`, null, syncHeaders());
      showToast('Photo deleted');
      setSelectedPhoto(null);
      fetchPhotos();
      fetchFilterOptions();
    } catch (err) {
      showToast('Failed to delete photo');
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.size) return;
    if (!window.confirm(`Delete ${selectedIds.size} photo(s)?`)) return;
    for (const id of selectedIds) {
      try {
        await authenticatedApiCall(instance, accounts, 'DELETE', `/technical-photos/photo/${id}`, null, syncHeaders());
      } catch (e) { /* continue */ }
    }
    setSelectedIds(new Set());
    showToast(`${selectedIds.size} photo(s) deleted`);
    fetchPhotos();
    fetchFilterOptions();
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === photos.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(photos.map(p => p.id)));
    }
  };

  const photoCount = photos.length;
  const hasFilters = categoryFilter || uploaderFilter || searchTerm;

  const getThumbnailUrl = useCallback((photo) => {
    if (!photo.sharepoint_item_id) return null;
    return `/api/technical-photos/photo/${photo.id}/thumbnail`;
  }, []);

  // Detail modal
  if (selectedPhoto) {
    return (
      <PhotoDetailModal
        photo={selectedPhoto}
        projectId={projectId}
        onClose={() => { setSelectedPhoto(null); fetchPhotos(); }}
        onDelete={() => handleDeletePhoto(selectedPhoto.id)}
        onUpdated={() => fetchPhotos()}
        viewers={viewers}
        isConnected={isConnected}
        connectionState={connectionState}
      />
    );
  }

  const btnStyle = {
    padding: '7px 14px',
    backgroundColor: 'white',
    color: '#374151',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    transition: 'all 0.15s ease',
  };

  const inputStyle = {
    padding: '7px 10px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '13px',
    outline: 'none',
  };

  return (
    <div style={{ padding: '1rem', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#111827', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            Photos
            <span style={{ fontSize: '14px', fontWeight: 400, color: '#6b7280' }}>
              {photoCount > 0 ? `(${photoCount})` : ''}
            </span>
          </h2>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '13px' }}>
            Upload and manage project photos. Add tags and references to link photos to RFIs, ISO sheets, and other items.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' }}>
          <ActiveViewers viewers={viewers} isConnected={isConnected} connectionState={connectionState} maxVisible={4} showConnectionStatus={false} />
          <button onClick={openUploadModal} style={{ ...btnStyle, backgroundColor: '#2563eb', color: 'white', borderColor: '#2563eb' }}>
            + Add Photos
          </button>
          {selectedIds.size > 0 && (
            <button onClick={handleBulkDelete} style={{ ...btnStyle, backgroundColor: '#dc2626', color: 'white', borderColor: '#dc2626' }}>
              Delete ({selectedIds.size})
            </button>
          )}
          <button onClick={fetchPhotos} style={btnStyle} title="Refresh">Refresh</button>
        </div>
      </div>

      {/* Filter / Sort Bar */}
      <div style={{
        display: 'flex', gap: '8px', marginBottom: '1rem', flexWrap: 'wrap', alignItems: 'center',
        padding: '10px 12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb',
      }}>
        <input
          type="text"
          placeholder="Search photos..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ ...inputStyle, width: '200px', flex: '0 1 200px' }}
        />
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} style={{ ...inputStyle, minWidth: '130px' }}>
          <option value="">All Categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={uploaderFilter} onChange={(e) => setUploaderFilter(e.target.value)} style={{ ...inputStyle, minWidth: '130px' }}>
          <option value="">All Uploaders</option>
          {uploaders.map(u => <option key={u} value={u}>{u}</option>)}
        </select>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ fontSize: '12px', color: '#6b7280' }}>Sort:</span>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ ...inputStyle, minWidth: '110px' }}>
            {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <button
            onClick={() => setSortOrder(prev => prev === 'desc' ? 'asc' : 'desc')}
            style={{ ...btnStyle, padding: '6px 8px', fontSize: '14px' }}
            title={sortOrder === 'desc' ? 'Descending' : 'Ascending'}
          >
            {sortOrder === 'desc' ? '\u2193' : '\u2191'}
          </button>
        </div>
        {hasFilters && (
          <button
            onClick={() => { setSearchTerm(''); setCategoryFilter(''); setUploaderFilter(''); }}
            style={{ ...btnStyle, fontSize: '12px', padding: '5px 10px', color: '#dc2626', borderColor: '#fca5a5' }}
          >
            Clear Filters
          </button>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '2px' }}>
          <button
            onClick={() => setViewMode('grid')}
            style={{ ...btnStyle, padding: '6px 10px', backgroundColor: viewMode === 'grid' ? '#e5e7eb' : 'white' }}
            title="Grid View"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg>
          </button>
          <button
            onClick={() => setViewMode('list')}
            style={{ ...btnStyle, padding: '6px 10px', backgroundColor: viewMode === 'list' ? '#e5e7eb' : 'white' }}
            title="List View"
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><rect x="1" y="2" width="14" height="2.5" rx="1"/><rect x="1" y="6.75" width="14" height="2.5" rx="1"/><rect x="1" y="11.5" width="14" height="2.5" rx="1"/></svg>
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: '10px 14px', marginBottom: '1rem', backgroundColor: '#fef2f2', color: '#991b1b', borderRadius: '6px', fontSize: '13px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <button onClick={() => { setError(null); fetchPhotos(); }} style={{ ...btnStyle, fontSize: '12px', padding: '4px 10px', color: '#991b1b', borderColor: '#991b1b' }}>Retry</button>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, padding: '12px 20px', backgroundColor: '#374151', color: 'white', borderRadius: '8px', fontSize: '14px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', zIndex: 9999 }}>
          {toast}
        </div>
      )}

      {/* Content */}
      <div style={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>Loading photos...</div>
        ) : photos.length === 0 ? (
          <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280', backgroundColor: '#f9fafb', borderRadius: '12px', border: '2px dashed #d1d5db' }}>
            <div style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.4 }}>&#128247;</div>
            <p style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 500 }}>
              {hasFilters ? 'No photos match your filters' : 'No photos yet'}
            </p>
            <p style={{ margin: 0, fontSize: '13px' }}>
              {hasFilters ? 'Try adjusting your filters or search term.' : 'Click "Add Photos" to upload images for this project.'}
            </p>
          </div>
        ) : viewMode === 'grid' ? (
          /* Grid View */
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px' }}>
            {photos.map(photo => (
              <div
                key={photo.id}
                style={{
                  border: selectedIds.has(photo.id) ? '2px solid #2563eb' : '1px solid #e5e7eb',
                  borderRadius: '10px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  backgroundColor: 'white',
                  transition: 'box-shadow 0.2s, transform 0.15s',
                  boxShadow: selectedIds.has(photo.id) ? '0 0 0 2px rgba(37,99,235,0.2)' : '0 1px 3px rgba(0,0,0,0.06)',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.boxShadow = selectedIds.has(photo.id) ? '0 0 0 2px rgba(37,99,235,0.2)' : '0 1px 3px rgba(0,0,0,0.06)'; e.currentTarget.style.transform = 'none'; }}
                onClick={() => setSelectedPhoto(photo)}
              >
                {/* Thumbnail */}
                <div style={{ height: '180px', overflow: 'hidden', position: 'relative', backgroundColor: '#f3f4f6' }}>
                  <img
                    src={getThumbnailUrl(photo)}
                    alt={photo.title || photo.file_name || ''}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                    loading="lazy"
                    onError={(e) => { e.target.style.display = 'none'; e.target.parentElement.innerHTML = '<div style="height:100%;display:flex;align-items:center;justify-content:center;color:#9ca3af;font-size:40px">&#128247;</div>'; }}
                  />
                  {/* Select checkbox */}
                  <div
                    style={{ position: 'absolute', top: 8, left: 8, zIndex: 2 }}
                    onClick={(e) => { e.stopPropagation(); toggleSelect(photo.id); }}
                  >
                    <div style={{
                      width: 22, height: 22, borderRadius: '4px',
                      border: selectedIds.has(photo.id) ? '2px solid #2563eb' : '2px solid rgba(255,255,255,0.8)',
                      backgroundColor: selectedIds.has(photo.id) ? '#2563eb' : 'rgba(255,255,255,0.5)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'white', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer',
                      backdropFilter: 'blur(4px)',
                    }}>
                      {selectedIds.has(photo.id) ? '\u2713' : ''}
                    </div>
                  </div>
                  {/* Category badge */}
                  {photo.category && (
                    <div style={{
                      position: 'absolute', top: 8, right: 8,
                      padding: '2px 8px', backgroundColor: 'rgba(0,0,0,0.6)', color: 'white',
                      borderRadius: '12px', fontSize: '11px', fontWeight: 500,
                      backdropFilter: 'blur(4px)',
                    }}>
                      {photo.category}
                    </div>
                  )}
                </div>
                {/* Info */}
                <div style={{ padding: '10px 12px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827', marginBottom: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {photo.title || photo.file_name || 'Untitled'}
                  </div>
                  {photo.description && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {photo.description}
                    </div>
                  )}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: '#9ca3af' }}>
                    <span>{formatDate(photo.date_taken || photo.created_at)}</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {photo.tags && photo.tags.length > 0 && (
                        <span style={{ padding: '1px 6px', backgroundColor: '#eff6ff', color: '#2563eb', borderRadius: '8px', fontSize: '10px', fontWeight: 600 }}>
                          {photo.tags.length} ref{photo.tags.length !== 1 ? 's' : ''}
                        </span>
                      )}
                      <span>{photo.uploaded_by_name?.split(' ')[0] || ''}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          /* List View */
          <div style={{ border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', backgroundColor: 'white' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                  <th style={{ padding: '8px 12px', textAlign: 'left', width: 36 }}>
                    <input type="checkbox" checked={selectedIds.size === photos.length && photos.length > 0} onChange={toggleSelectAll} />
                  </th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', width: 50 }}></th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Title</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Category</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Location</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Date Taken</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Date Added</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Uploaded By</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Size</th>
                  <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600 }}>Refs</th>
                </tr>
              </thead>
              <tbody>
                {photos.map(photo => (
                  <tr
                    key={photo.id}
                    style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer', backgroundColor: selectedIds.has(photo.id) ? '#eff6ff' : 'white' }}
                    onClick={() => setSelectedPhoto(photo)}
                  >
                    <td style={{ padding: '6px 12px' }} onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selectedIds.has(photo.id)} onChange={() => toggleSelect(photo.id)} />
                    </td>
                    <td style={{ padding: '4px 8px' }}>
                      <div style={{ width: 40, height: 40, borderRadius: '4px', overflow: 'hidden', backgroundColor: '#f3f4f6' }}>
                        <img
                          src={getThumbnailUrl(photo)}
                          alt=""
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          loading="lazy"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    </td>
                    <td style={{ padding: '8px 12px', color: '#111827', fontWeight: 500 }}>{photo.title || photo.file_name || 'Untitled'}</td>
                    <td style={{ padding: '8px 12px', color: '#6b7280' }}>{photo.category || ''}</td>
                    <td style={{ padding: '8px 12px', color: '#6b7280' }}>{photo.location || ''}</td>
                    <td style={{ padding: '8px 12px', color: '#6b7280' }}>{formatDate(photo.date_taken)}</td>
                    <td style={{ padding: '8px 12px', color: '#6b7280' }}>{formatDateTime(photo.created_at)}</td>
                    <td style={{ padding: '8px 12px', color: '#6b7280' }}>{photo.uploaded_by_name || ''}</td>
                    <td style={{ padding: '8px 12px', color: '#9ca3af' }}>{formatFileSize(photo.file_size)}</td>
                    <td style={{ padding: '8px 12px' }}>
                      {photo.tags && photo.tags.length > 0 ? (
                        <span style={{ padding: '1px 6px', backgroundColor: '#eff6ff', color: '#2563eb', borderRadius: '8px', fontSize: '11px', fontWeight: 600 }}>{photo.tags.length}</span>
                      ) : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9998 }}
          onClick={(e) => e.target === e.currentTarget && setShowUploadModal(false)}
        >
          <div
            style={{ backgroundColor: 'white', borderRadius: '12px', padding: '24px', maxWidth: '560px', width: '92%', maxHeight: '90vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 4px', fontSize: '18px', color: '#111827' }}>Add Photos</h3>
            <p style={{ margin: '0 0 16px', color: '#6b7280', fontSize: '13px' }}>
              Upload images to the project. You can select multiple files at once.
            </p>
            <form onSubmit={handleUploadSubmit}>
              {/* Drop zone */}
              <div
                style={{
                  border: `2px dashed ${dragOver ? '#2563eb' : '#d1d5db'}`,
                  borderRadius: '10px',
                  padding: '28px 16px',
                  textAlign: 'center',
                  marginBottom: '16px',
                  backgroundColor: dragOver ? '#eff6ff' : '#f9fafb',
                  transition: 'all 0.2s',
                }}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  handleFilesSelected(e.dataTransfer.files);
                }}
              >
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={(e) => handleFilesSelected(e.target.files)}
                  style={{ display: 'none' }}
                  id="photo-upload-input"
                />
                <label htmlFor="photo-upload-input" style={{ cursor: 'pointer', display: 'block' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.5 }}>&#128247;</div>
                  <div style={{ fontSize: '14px', color: '#374151', fontWeight: 500 }}>Drop images here or click to browse</div>
                  <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>JPEG, PNG, GIF, WebP, HEIC. Up to 25 MB each.</div>
                </label>
              </div>

              {/* File list */}
              {uploadFiles.length > 0 && (
                <div style={{ marginBottom: '16px', maxHeight: '140px', overflow: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
                  {uploadFiles.map((f, idx) => (
                    <div key={idx} style={{ display: 'flex', alignItems: 'center', padding: '6px 10px', borderBottom: idx < uploadFiles.length - 1 ? '1px solid #f3f4f6' : 'none', gap: '8px' }}>
                      <div style={{ width: 32, height: 32, borderRadius: '4px', overflow: 'hidden', backgroundColor: '#f3f4f6', flexShrink: 0 }}>
                        <img src={URL.createObjectURL(f)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <div style={{ flex: 1, overflow: 'hidden' }}>
                        <div style={{ fontSize: '12px', color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                        <div style={{ fontSize: '11px', color: '#9ca3af' }}>{formatFileSize(f.size)}</div>
                      </div>
                      <button type="button" onClick={() => removeUploadFile(idx)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px', padding: '2px 6px' }}>&times;</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Metadata fields */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '16px' }}>
                <div style={{ gridColumn: '1 / -1' }}>
                  <input
                    type="text"
                    placeholder="Title (optional)"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm(p => ({ ...p, title: e.target.value }))}
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <textarea
                    placeholder="Description (optional)"
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(p => ({ ...p, description: e.target.value }))}
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', resize: 'vertical', minHeight: '50px' }}
                    rows={2}
                  />
                </div>
                <input
                  type="text"
                  placeholder="Category (e.g. Piping, Structural)"
                  value={uploadForm.category}
                  onChange={(e) => setUploadForm(p => ({ ...p, category: e.target.value }))}
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                  list="category-suggestions"
                />
                <datalist id="category-suggestions">
                  {categories.map(c => <option key={c} value={c} />)}
                </datalist>
                <input
                  type="text"
                  placeholder="Location (optional)"
                  value={uploadForm.location}
                  onChange={(e) => setUploadForm(p => ({ ...p, location: e.target.value }))}
                  style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                />
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: '#6b7280', marginBottom: '2px' }}>Date Taken</label>
                  <input
                    type="date"
                    value={uploadForm.date_taken}
                    onChange={(e) => setUploadForm(p => ({ ...p, date_taken: e.target.value }))}
                    style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }}
                  />
                </div>
              </div>

              {/* References section */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: '#374151' }}>
                    References ({uploadRefs.length})
                  </label>
                  <button
                    type="button"
                    onClick={() => setUploadRefs(prev => [...prev, { linked_item_type: 'RFI', linked_item_id: '', description: '' }])}
                    style={{ ...btnStyle, fontSize: '12px', padding: '4px 10px' }}
                  >
                    + Add Reference
                  </button>
                </div>
                {uploadRefs.length === 0 && (
                  <p style={{ margin: 0, fontSize: '12px', color: '#9ca3af' }}>
                    Link this photo to RFIs, ISO sheets, transmittals, or other portal items.
                  </p>
                )}
                {uploadRefs.map((ref, idx) => (
                  <div key={idx} style={{ display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'flex-start' }}>
                    <select
                      value={ref.linked_item_type}
                      onChange={(e) => setUploadRefs(prev => prev.map((r, i) => i === idx ? { ...r, linked_item_type: e.target.value } : r))}
                      style={{ ...inputStyle, width: '130px', flexShrink: 0 }}
                    >
                      {REF_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <input
                      type="text"
                      placeholder="ID (e.g. RFI-001)"
                      value={ref.linked_item_id}
                      onChange={(e) => setUploadRefs(prev => prev.map((r, i) => i === idx ? { ...r, linked_item_id: e.target.value } : r))}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <input
                      type="text"
                      placeholder="Note (optional)"
                      value={ref.description}
                      onChange={(e) => setUploadRefs(prev => prev.map((r, i) => i === idx ? { ...r, description: e.target.value } : r))}
                      style={{ ...inputStyle, flex: 1 }}
                    />
                    <button
                      type="button"
                      onClick={() => setUploadRefs(prev => prev.filter((_, i) => i !== idx))}
                      style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: '16px', padding: '6px 4px', flexShrink: 0 }}
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>

              {uploadError && (
                <div style={{ marginBottom: '12px', padding: '8px 12px', backgroundColor: '#fef2f2', color: '#991b1b', borderRadius: '6px', fontSize: '13px' }}>{uploadError}</div>
              )}

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowUploadModal(false)} style={btnStyle}>Cancel</button>
                <button
                  type="submit"
                  disabled={!uploadFiles.length || uploading}
                  style={{ ...btnStyle, backgroundColor: '#2563eb', color: 'white', borderColor: '#2563eb', opacity: (!uploadFiles.length || uploading) ? 0.6 : 1 }}
                >
                  {uploading ? 'Uploading...' : `Upload ${uploadFiles.length} Photo${uploadFiles.length !== 1 ? 's' : ''}`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PhotosLog;
